// Conversor de unidades culinarias y de inventario para frontend
const massRatios: Record<string, number> = {
  g: 1.0,
  kg: 1000.0,
  mg: 0.001,
  lb: 500.0,
  oz: 28.3495,
};

const volumeRatios: Record<string, number> = {
  ml: 1.0,
  l: 1000.0,
  oz_fl: 29.5735,
  gal: 3785.41,
  taza: 240.0,
  cucharada: 15.0,
  cucharadita: 5.0,
};

const aliases: Record<string, string> = {
  g: 'g', gr: 'g', grs: 'g', gramo: 'g', gramos: 'g',
  kg: 'kg', kgr: 'kg', kilo: 'kg', kilos: 'kg', kilogramo: 'kg', kilogramos: 'kg',
  mg: 'mg', miligramo: 'mg', miligramos: 'mg',
  lb: 'lb', lbs: 'lb', libra: 'lb', libras: 'lb',
  oz: 'oz', onza: 'oz', onzas: 'oz',

  ml: 'ml', cc: 'ml', cm3: 'ml', mililitro: 'ml', mililitros: 'ml',
  l: 'l', lt: 'l', lts: 'l', litro: 'l', litros: 'l',
  oz_fl: 'oz_fl', fl_oz: 'oz_fl', onza_liquida: 'oz_fl', onzas_liquidas: 'oz_fl',
  gal: 'gal', galon: 'gal', galones: 'gal',
  taza: 'taza', tazas: 'taza', cup: 'taza',
  cucharada: 'cucharada', cucharadas: 'cucharada', tbsp: 'cucharada', cda: 'cucharada',
  cucharadita: 'cucharadita', cucharaditas: 'cucharadita', tsp: 'cucharadita', cdta: 'cucharadita',

  und: 'und', unds: 'und', u: 'und', unidad: 'und', unidades: 'und',
  porcion: 'und', porciones: 'und',
  caja: 'caja', cajas: 'caja',
  paquete: 'paquete', paquetes: 'paquete',
  botella: 'botella', botellas: 'botella',
  lata: 'lata', latas: 'lata',
};

export function normalizeUnit(unit: string): string {
  if (!unit) return '';
  const clean = unit.trim().toLowerCase()
    .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    .replace(/\s+/g, '_');
  return aliases[clean] || clean;
}

export function convertQuantity(qty: number, fromUnit: string, toUnit: string, conversionFactor = 1): number {
  if (!qty || qty <= 0) return 0;
  const from = normalizeUnit(fromUnit);
  const to = normalizeUnit(toUnit);

  if (from === to) return qty;

  // 1. Masa
  if (massRatios[from] && massRatios[to]) {
    const inGrams = qty * massRatios[from];
    return inGrams / massRatios[to];
  }

  // 2. Volumen
  if (volumeRatios[from] && volumeRatios[to]) {
    const inMl = qty * volumeRatios[from];
    return inMl / volumeRatios[to];
  }

  // 3. Conteo / Empaques
  const factor = conversionFactor > 0 ? conversionFactor : 1;
  if ((from === 'caja' || from === 'paquete') && to === 'und') {
    return qty * factor;
  }
  if (from === 'und' && (to === 'caja' || to === 'paquete')) {
    return qty / factor;
  }

  // 4. Aproximación culinaria Masa <-> Volumen (1 ml ~ 1 g)
  if (volumeRatios[from] && massRatios[to]) {
    const inGrams = qty * volumeRatios[from];
    return inGrams / massRatios[to];
  }
  if (massRatios[from] && volumeRatios[to]) {
    const inMl = qty * massRatios[from];
    return inMl / volumeRatios[to];
  }

  return qty;
}

export function calculateIngredientCost(
  recipeQty: number,
  recipeUnit: string,
  itemCost: number,
  itemUnit: string,
  conversionFactor = 1,
  wastePct = 0
): { netQtyInItemUnit: number; grossQtyInItemUnit: number; cost: number } {
  const netQty = convertQuantity(recipeQty, recipeUnit, itemUnit, conversionFactor);
  const wasteFactor = (wastePct > 0 && wastePct < 100) ? (1 - (wastePct / 100)) : 1;
  const grossQty = wasteFactor > 0 ? (netQty / wasteFactor) : netQty;
  const cost = grossQty * itemCost;

  return {
    netQtyInItemUnit: Number(netQty.toFixed(4)),
    grossQtyInItemUnit: Number(grossQty.toFixed(4)),
    cost: Number(cost.toFixed(2)),
  };
}
